/**
 * Build structured payload for naturals salon report PDF (ReportLab).
 * Mirrors Skin Routine on-screen sections without DOM screenshot capture.
 */

import { toPdfDataUrl, mapProductImagesToDataUrls } from './pdfImagePayload';
import {
  cropConcernCircle,
  circleFitImage,
  findBestBboxForConcern,
} from './concernCropUtils';
import {
  getDetailedSeverityLabel,
  buildDetailedConcernBlurb,
} from './reportSeverity';
import {
  canonicalSkinConditionName,
  isSkinConditionName,
} from './skinResultsUtils';
import { fetchSkinConcernMask, conditionKeyFromDisplay } from './lazySkinMask';
import { getSkinConditionKeyCandidates } from './maskUtils';
import { normalizeDetections } from './skinConditionNormalization';
import { getPageSetupDataKey } from '../components/PageBackgroundSync';
import { getFranchiseOrganizationId, getFranchiseId } from '../../shared/franchiseFlowAuth';
import {
  readStoredFranchiseBrandLogo,
  readStoredFranchiseBrandName,
} from '../../shared/franchiseBrand';

const DEFAULT_BG_PRIMARY = '#fffdf9';
const DEFAULT_BG_SECONDARY = '#f3e8dc';

function readCssColor(varName, fallback) {
  try {
    if (typeof document === 'undefined') return fallback;
    const roots = [document.documentElement];
    const shell = document.getElementById('skin-routine-page-shell')
      || document.querySelector('.skin-routine-page-shell.page-bg')
      || document.querySelector('.page-bg');
    if (shell) roots.unshift(shell);
    for (const el of roots) {
      const v = getComputedStyle(el).getPropertyValue(varName).trim();
      if (v) return v;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

function rgbTripletToHex(triplet, fallback) {
  const m = String(triplet || '').match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!m) return fallback;
  const hex = (n) => Number(n).toString(16).padStart(2, '0');
  return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
}

function normalizeCssColor(color, fallback) {
  const raw = String(color || '').trim();
  if (!raw) return fallback;
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
    if (raw.length === 4) {
      return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
    }
    return raw;
  }
  const rgb = raw.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const hex = (n) => Number(n).toString(16).padStart(2, '0');
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }
  if (/^\d+\s*,\s*\d+\s*,\s*\d+$/.test(raw)) {
    return rgbTripletToHex(raw, fallback);
  }
  return fallback;
}

/**
 * Build page_setup payload from Workspace Page Setup (org theme).
 * Prefer sessionStorage page-setup; fall back to live CSS vars; then workspace defaults.
 */
export function resolveSalonReportPageSetup({ token, orgName, orgLogoUrl, organizationId } = {}) {
  let background = {
    primary_color: DEFAULT_BG_PRIMARY,
    secondary_color: DEFAULT_BG_SECONDARY,
    type: 'gradient',
    gradient_angle: 180,
  };
  let fonts = null;
  let cta = null;
  let borders = null;
  let highlight = null;
  let resolvedOrg = orgName || readStoredFranchiseBrandName() || null;
  let resolvedLogo = orgLogoUrl || readStoredFranchiseBrandLogo() || null;
  let resolvedOrgId = organizationId || null;
  let themeSource = 'default';

  try {
    const key = getPageSetupDataKey(token);
    const raw = key ? sessionStorage.getItem(key) : null;
    if (raw) {
      const result = JSON.parse(raw);
      const ext = result?.external_fields || result?.data?.external_fields || {};
      const bg = ext.background && typeof ext.background === 'object' ? ext.background : {};
      const primary = normalizeCssColor(
        bg.primary_color || bg.primaryColor || ext.primary_color,
        '',
      );
      const secondary = normalizeCssColor(
        bg.secondary_color || bg.secondaryColor || ext.secondary_color,
        '',
      );
      if (primary || secondary || ext.fonts || ext.cta || ext.borders) {
        themeSource = 'workspace_page_setup';
      }
      if (primary || secondary) {
        background = {
          primary_color: primary || secondary || background.primary_color,
          secondary_color: secondary || primary || background.secondary_color,
          type: String(bg.type || 'gradient').toLowerCase(),
          gradient_angle: Number.isFinite(Number(bg.gradient_angle)) ? Number(bg.gradient_angle) : 180,
        };
      }
      if (ext.fonts && typeof ext.fonts === 'object') fonts = ext.fonts;
      if (ext.cta && typeof ext.cta === 'object') cta = ext.cta;
      if (ext.borders && typeof ext.borders === 'object') borders = ext.borders;
      if (ext.highlight != null) highlight = ext.highlight;
      if (!resolvedOrg) {
        resolvedOrg = String(
          ext.outlet_name
            || ext.franchise_name
            || ext.salon_name
            || ext.organization_name
            || ext.org_name
            || result?.organization_name
            || '',
        ).trim() || null;
      }
      const setupLogo = ext.franchise_logo || ext.org_logo;
      if (!resolvedLogo && typeof setupLogo === 'string' && setupLogo.trim()) {
        resolvedLogo = setupLogo.trim();
      }
      if (result?.organization_id != null) {
        resolvedOrgId = result.organization_id;
      }
    }
  } catch {
    /* keep defaults */
  }

  // CSS vars only when page-setup missing (still applied live by PageBackgroundSync)
  if (themeSource !== 'workspace_page_setup') {
    const cssPrimary = normalizeCssColor(
      readCssColor('--page-bg-primary', '') || rgbTripletToHex(readCssColor('--page-bg-primary-rgb', ''), ''),
      '',
    );
    const cssSecondary = normalizeCssColor(
      readCssColor('--page-bg-secondary', '') || rgbTripletToHex(readCssColor('--page-bg-secondary-rgb', ''), ''),
      '',
    );
    if (cssPrimary || cssSecondary) {
      background = {
        primary_color: cssPrimary || cssSecondary || background.primary_color,
        secondary_color: cssSecondary || cssPrimary || background.secondary_color,
        type: 'gradient',
        gradient_angle: 180,
      };
      themeSource = 'css';
    }
    const fontPrimary = normalizeCssColor(readCssColor('--skin-text-primary', ''), '');
    const fontSecondary = normalizeCssColor(readCssColor('--skin-text-secondary', ''), '');
    const fontMuted = normalizeCssColor(readCssColor('--skin-text-muted', ''), '');
    if (fontPrimary || fontSecondary || fontMuted) {
      fonts = {
        primary: fontPrimary || undefined,
        secondary: fontSecondary || undefined,
        muted: fontMuted || undefined,
      };
    }
    const ctaPrimary = normalizeCssColor(readCssColor('--cta-primary', ''), '');
    if (ctaPrimary) {
      cta = {
        primary_color: ctaPrimary,
        border_color: normalizeCssColor(readCssColor('--cta-border-color', ''), ctaPrimary),
        text_color: normalizeCssColor(readCssColor('--cta-text-color', ''), '#ffffff'),
      };
    }
    const borderColor = normalizeCssColor(readCssColor('--skin-border-color', ''), '');
    if (borderColor) borders = { color: borderColor };
    const hl = normalizeCssColor(readCssColor('--skin-highlight', ''), '');
    if (hl) highlight = hl;
  }

  try {
    if (!resolvedOrgId) {
      const oid = getFranchiseOrganizationId?.();
      if (oid) resolvedOrgId = oid;
    }
  } catch {
    /* optional */
  }

  return {
    org_logo: resolvedLogo,
    org_name: resolvedOrg,
    organization_id: resolvedOrgId,
    background,
    fonts: fonts || undefined,
    cta: cta || undefined,
    borders: borders || undefined,
    highlight: highlight || undefined,
    theme_source: themeSource,
  };
}

function resolveExistingMaskUrl(skinConditions, displayName, apiBaseUrl) {
  const canonical = canonicalSkinConditionName(displayName);
  if (!canonical || !skinConditions || typeof skinConditions !== 'object') return null;
  const altKeys = [
    conditionKeyFromDisplay(canonical),
    ...getSkinConditionKeyCandidates(canonical),
    canonical,
    canonical.toLowerCase(),
    String(displayName || '').toLowerCase().replace(/\s+/g, '_'),
  ].filter(Boolean);
  let maskUrl = null;
  for (const k of Array.from(new Set(altKeys))) {
    const entry = skinConditions[k];
    if (entry && typeof entry === 'object' && entry.mask) {
      maskUrl = entry.mask;
      break;
    }
    if (typeof entry === 'string' && entry) {
      maskUrl = entry;
      break;
    }
  }
  if (typeof maskUrl !== 'string' || !maskUrl) return null;
  if (maskUrl.startsWith('/') && apiBaseUrl) {
    return `${String(apiBaseUrl).replace(/\/$/, '')}${maskUrl}`;
  }
  return maskUrl;
}

function lookupDescription(descriptions, item, key) {
  if (!descriptions || typeof descriptions !== 'object') return '';
  const candidates = [
    item?.class_name,
    item?.label,
    key,
    canonicalSkinConditionName(item?.class_name),
    canonicalSkinConditionName(item?.label),
  ].filter(Boolean);
  for (const c of candidates) {
    const val = descriptions[c] || descriptions[String(c).toLowerCase()];
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (val && typeof val === 'object') {
      const text = val.description || val.text || val.summary || '';
      if (String(text).trim()) return String(text).trim();
    }
  }
  return '';
}

async function buildConcernCrops({
  items,
  imageSrc,
  detections,
  descriptions,
  skinConditions,
  sessionId,
  token,
  apiBaseUrl,
  maxItems = 6,
}) {
  const slice = (Array.isArray(items) ? items : []).slice(0, maxItems);
  const dets = normalizeDetections(detections || []);
  const out = [];

  await Promise.all(slice.map(async (item) => {
    const key = item.class_name || item.key;
    const label = item.label || item.class_name || key || 'Concern';
    let image = null;

    try {
      if (isSkinConditionName(key) || isSkinConditionName(label)) {
        const canonical = canonicalSkinConditionName(key) || canonicalSkinConditionName(label);
        let maskUrl = resolveExistingMaskUrl(skinConditions, canonical || label, apiBaseUrl);
        if (!maskUrl && sessionId && canonical) {
          try {
            maskUrl = await fetchSkinConcernMask({
              sessionId,
              displayName: canonical,
              appSessionToken: token,
            });
          } catch {
            maskUrl = null;
          }
        }
        if (maskUrl) {
          image = await circleFitImage(maskUrl, 160);
        }
      }
      if (!image && imageSrc) {
        const bbox = findBestBboxForConcern(dets, key) || findBestBboxForConcern(dets, label);
        if (bbox) {
          image = await cropConcernCircle(imageSrc, bbox, 160);
        }
        if (!image) {
          image = await circleFitImage(imageSrc, 160);
        }
      }
    } catch {
      image = null;
    }

    const description = lookupDescription(descriptions, item, key);
    const score = Number.isFinite(Number(item?.score)) ? Number(item.score) : null;
    out.push({
      class_name: key || label,
      label,
      score,
      color: item?.color || null,
      severity: getDetailedSeverityLabel(score) || null,
      description: buildDetailedConcernBlurb(item, description) || description || null,
      image,
    });
  }));

  return out;
}

function slimMacroForPdf(macro) {
  if (!macro?.regions || typeof macro.regions !== 'object') return null;
  const regions = {};
  for (const [key, region] of Object.entries(macro.regions)) {
    if (!region || typeof region !== 'object') continue;
    const scores = region.scores || region.macro_analysis?.scores || null;
    const observations = Array.isArray(region.observations)
      ? region.observations
      : (region.macro_analysis?.observations || []);
    const limitations = Array.isArray(region.limitations)
      ? region.limitations
      : (region.macro_analysis?.limitations || []);
    regions[key] = {
      original: region.original || null,
      overlay: region.overlay || null,
      visual_summary: region.visual_summary || region.macro_analysis?.visual_summary || null,
      insight: region.insight || region.macro_analysis?.insight || null,
      conditions: Array.isArray(region.conditions)
        ? region.conditions.slice(0, 6)
        : (Array.isArray(region.macro_analysis?.conditions) ? region.macro_analysis.conditions.slice(0, 6) : []),
      scores: scores && typeof scores === 'object' ? scores : null,
      observations: Array.isArray(observations) ? observations.slice(0, 5) : [],
      limitations: Array.isArray(limitations) ? limitations.slice(0, 4) : [],
      image_quality: region.image_quality || region.macro_analysis?.image_quality || null,
      engagement_mask: region.engagement_mask?.image
        ? { image: region.engagement_mask.image, high_attention_regions: region.engagement_mask.high_attention_regions }
        : (region.macro_analysis?.engagement_mask?.image
          ? { image: region.macro_analysis.engagement_mask.image, high_attention_regions: region.macro_analysis.engagement_mask.high_attention_regions }
          : null),
    };
  }
  if (!Object.keys(regions).length) return null;
  return {
    summary: macro.summary || null,
    regions,
  };
}

async function compressMacroImages(macro) {
  if (!macro?.regions) return macro;
  const next = { ...macro, regions: {} };
  for (const [key, region] of Object.entries(macro.regions)) {
    const original = await toPdfDataUrl(region.original, { maxEdge: 480, quality: 0.8 }).catch(() => null);
    const overlay = await toPdfDataUrl(region.overlay, { maxEdge: 480, quality: 0.8 }).catch(() => null);
    next.regions[key] = {
      ...region,
      original: original || region.original || null,
      overlay: overlay || region.overlay || null,
    };
  }
  return next;
}

/**
 * @param {object} ctx — live SkinRoutinePage values
 */
export async function buildSalonReportPdfPayload(ctx = {}) {
  const {
    sessionId,
    token,
    name = 'Guest',
    useStoredReport = true,
    summary = '',
    routine = null,
    skinScore = null,
    gender = null,
    skinType = null,
    skinAge = null,
    aqiData = null,
    envTip = null,
    healthScoreItems = [],
    improvementPotential = null,
    displayTips = [],
    displayInsight = '',
    userImage = null,
    previewBaseImage = null,
    detections = [],
    skinConditions = null,
    aiConditionDescriptions = {},
    recommendedProducts = { morning: [], evening: [] },
    recommendedTreatments = [],
    displayMacro = null,
    orgName = null,
    orgLogoUrl = null,
    analysisDateText = null,
    reportIdText = null,
    apiBaseUrl = '',
  } = ctx;

  const pageSetupEarly = resolveSalonReportPageSetup({ token, orgName, orgLogoUrl });
  const franchiseIdEarly = getFranchiseId() || null;
  if (useStoredReport && sessionId) {
    return {
      session_id: sessionId,
      app_session_token: token || null,
      use_stored_report: true,
      name: name || 'Guest',
      org_name: pageSetupEarly.org_name || orgName || readStoredFranchiseBrandName() || null,
      org_logo_url: pageSetupEarly.org_logo || orgLogoUrl || readStoredFranchiseBrandLogo() || null,
      organization_id: pageSetupEarly.organization_id || getFranchiseOrganizationId() || null,
      franchise_id: franchiseIdEarly,
      page_setup: {
        ...pageSetupEarly,
        ...(franchiseIdEarly ? { franchise_id: franchiseIdEarly } : {}),
      },
    };
  }

  const faceSrc = previewBaseImage || userImage;
  const user_image = faceSrc
    ? await toPdfDataUrl(faceSrc, { maxEdge: 720, quality: 0.85 }).catch(() => faceSrc)
    : null;

  const severity = [];
  const good = [];
  for (const item of healthScoreItems) {
    const entry = {
      name: item.label || item.class_name,
      class_name: item.class_name,
      label: item.label || item.class_name,
      score: item.score,
      color: item.color,
      confidence: item.confidence,
      is_condition: item.is_condition,
    };
    const sc = Number(item.score);
    if (Number.isFinite(sc) && sc < 75) severity.push(entry);
    else good.push(entry);
  }

  const concern_crops = await buildConcernCrops({
    items: healthScoreItems,
    imageSrc: faceSrc,
    detections,
    descriptions: aiConditionDescriptions,
    skinConditions,
    sessionId,
    token,
    apiBaseUrl,
    maxItems: 16,
  });

  let macro = slimMacroForPdf(displayMacro);
  if (macro) {
    macro = await compressMacroImages(macro);
  }

  const morning = await mapProductImagesToDataUrls(recommendedProducts?.morning || [], 6);
  const evening = await mapProductImagesToDataUrls(recommendedProducts?.evening || [], 6);

  const treatments = [];
  for (const t of (Array.isArray(recommendedTreatments) ? recommendedTreatments : []).slice(0, 8)) {
    const src = t?.image_url || t?.imageUrl || t?.thumbnail_url || null;
    let image_url = src;
    try {
      image_url = (await toPdfDataUrl(src, { maxEdge: 320, quality: 0.8 })) || src;
    } catch {
      image_url = src;
    }
    treatments.push({ ...t, image_url });
  }

  const ageVal = skinAge?.estimated_age ?? skinAge?.skin_age ?? null;
  const pageSetup = resolveSalonReportPageSetup({ token, orgName, orgLogoUrl });
  const franchiseId = getFranchiseId() || null;

  return {
    session_id: sessionId,
    app_session_token: token || null,
    organization_id: pageSetup.organization_id || getFranchiseOrganizationId() || null,
    franchise_id: franchiseId,
    name: name || 'Guest',
    summary: summary || '',
    routine: routine || { morning: [], evening: [] },
    skin_score: skinScore ?? null,
    gender: gender && gender !== 'Not specified' ? gender : null,
    skin_type: skinType || null,
    age: ageVal != null && !Number.isNaN(Number(ageVal)) ? Number(ageVal) : null,
    skin_age_explanation: typeof skinAge?.explanation === 'string' ? skinAge.explanation : null,
    aqi_data: aqiData || null,
    env_tip: envTip || null,
    skin_concerns: { severity, good },
    concern_crops,
    priority_tips: Array.isArray(displayTips) ? displayTips.slice(0, 6) : [],
    priority_insight: displayInsight || null,
    improvement_potential: improvementPotential,
    macro,
    user_image,
    client_products: { morning, evening },
    client_treatments: treatments,
    org_name: pageSetup.org_name || orgName || readStoredFranchiseBrandName() || null,
    analysis_date: analysisDateText || null,
    report_id: reportIdText || null,
    page_setup: {
      ...pageSetup,
      ...(franchiseId ? { franchise_id: franchiseId } : {}),
    },
  };
}
